/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Card,
    CardMedia,
    Typography,
    Button,
    Chip,
    Skeleton,
    Rating,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    Hotel,
    Flight,
    DirectionsCar,
    Attractions,
    Security,
    ChevronRight,
    CheckCircle,
} from '@mui/icons-material';
import { Ad, AdPage, AdCategory } from '../../data/mockAds';
import { useAds } from '../../contexts/AdContext';
import AdDrawer from './AdDrawer';

// ============================================================================
// TYPES
// ============================================================================

interface InFeedAdProps {
    page: AdPage;
    index?: number; // Position in feed for tracking
    contextCategory?: AdCategory;
    variant?: 'compact' | 'standard' | 'large';
}

// ============================================================================
// CATEGORY ICONS
// ============================================================================

const categoryIcons: Record<AdCategory, React.ReactElement> = {
    hotel: <Hotel sx={{ fontSize: 16 }} />,
    flight: <Flight sx={{ fontSize: 16 }} />,
    car: <DirectionsCar sx={{ fontSize: 16 }} />,
    activity: <Attractions sx={{ fontSize: 16 }} />,
    insurance: <Security sx={{ fontSize: 16 }} />,
    general: <Attractions sx={{ fontSize: 16 }} />,
};

// ============================================================================
// COMPONENT
// ============================================================================

const InFeedAd: React.FC<InFeedAdProps> = ({
    page,
    index = 0,
    contextCategory,
    variant = 'standard',
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    
    const {
        shouldShowAds,
        getAdsForPage,
        trackImpression,
        trackClick,
        isDelayPassed,
    } = useAds();
    
    const [currentAd, setCurrentAd] = useState<Ad | null>(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [hasTrackedImpression, setHasTrackedImpression] = useState(false);
    
    // Check if ads should show
    const showAds = shouldShowAds(page);
    
    // Load ad based on index (for variety in feed)
    useEffect(() => {
        if (!showAds || !isDelayPassed) {
            setLoading(false);
            return;
        }
        
        // Simulate loading delay
        const timer = setTimeout(() => {
            const ads = getAdsForPage(page, 'in_feed', contextCategory);
            if (ads.length > 0) {
                // Use index to select different ads in feed
                const adIndex = index % ads.length;
                setCurrentAd(ads[adIndex]);
            }
            setLoading(false);
        }, 300);
        
        return () => clearTimeout(timer);
    }, [showAds, isDelayPassed, page, index, contextCategory, getAdsForPage]);
    
    // Track impression when ad is visible
    useEffect(() => {
        if (currentAd && !hasTrackedImpression && isDelayPassed) {
            trackImpression(currentAd, page, 'in_feed');
            setHasTrackedImpression(true);
        }
    }, [currentAd, hasTrackedImpression, isDelayPassed, page, trackImpression]);
    
    // Handle ad click
    const handleClick = useCallback(() => {
        if (currentAd) {
            trackClick(currentAd, page, 'in_feed', 'open_drawer');
            setDrawerOpen(true);
        }
    }, [currentAd, page, trackClick]);
    
    // Don't render if ads disabled
    if (!showAds) return null;
    
    // Loading state
    if (loading) {
        return (
            <Skeleton
                variant="rounded"
                height={variant === 'compact' ? 120 : variant === 'large' ? 220 : 160}
                sx={{ borderRadius: 3, my: 2 }}
            />
        );
    }
    
    // No ad available
    if (!currentAd) return null;
    
    const CategoryIcon = categoryIcons[currentAd.category];
    
    // Compact variant (mobile-friendly, minimal)
    if (variant === 'compact') {
        return (
            <>
                <Card
                    onClick={handleClick}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 1.5,
                        my: 2,
                        borderRadius: 2,
                        cursor: 'pointer',
                        border: '1px solid #e0e0e0',
                        bgcolor: '#fafafa',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            borderColor: '#2D6E6A',
                            bgcolor: '#fff',
                        },
                    }}
                >
                    {/* Image */}
                    <CardMedia
                        component="img"
                        sx={{
                            width: 80,
                            height: 80,
                            borderRadius: 2,
                            objectFit: 'cover',
                            flexShrink: 0,
                        }}
                        image={currentAd.image}
                        alt={currentAd.title}
                    />
                    
                    {/* Content */}
                    <Box sx={{ flex: 1, ml: 2, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Chip
                                label="Sponsored"
                                size="small"
                                sx={{
                                    bgcolor: '#2D6E6A',
                                    color: 'white',
                                    fontWeight: 600,
                                    fontSize: '0.6rem',
                                    height: 18,
                                }}
                            />
                            {CategoryIcon}
                        </Box>
                        <Typography
                            variant="subtitle2"
                            sx={{
                                fontWeight: 600,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {currentAd.title}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{ color: '#666' }}
                        >
                            {currentAd.inlineContent.price}
                        </Typography>
                    </Box>
                    
                    {/* Arrow */}
                    <ChevronRight sx={{ color: '#2D6E6A', flexShrink: 0 }} />
                </Card>
                
                <AdDrawer
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    ad={currentAd}
                    page={page}
                    placement="in_feed"
                />
            </>
        );
    }
    
    // Standard variant
    return (
        <>
            <Card
                onClick={handleClick}
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    my: 2,
                    borderRadius: 3,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    border: '1px solid #e0e0e0',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        borderColor: '#2D6E6A',
                        boxShadow: '0 4px 12px rgba(0,180,160,0.1)',
                    },
                }}
            >
                {/* Image */}
                <Box sx={{ position: 'relative', width: { xs: '100%', sm: 200 }, flexShrink: 0 }}>
                    <CardMedia
                        component="img"
                        sx={{
                            height: { xs: 140, sm: '100%' },
                            minHeight: { sm: 160 },
                            objectFit: 'cover',
                        }}
                        image={currentAd.image}
                        alt={currentAd.title}
                    />
                    <Chip
                        label="Sponsored"
                        size="small"
                        sx={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            bgcolor: '#2D6E6A',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.65rem',
                            height: 20,
                        }}
                    />
                </Box>
                
                {/* Content */}
                <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Chip
                            icon={CategoryIcon}
                            label={currentAd.category}
                            size="small"
                            sx={{
                                height: 22,
                                fontSize: '0.7rem',
                                textTransform: 'capitalize',
                                bgcolor: '#f0f0f0',
                            }}
                        />
                        {currentAd.tier === 'premium' && (
                            <Chip
                                label="⭐ Premium"
                                size="small"
                                sx={{
                                    height: 20,
                                    fontSize: '0.65rem',
                                    bgcolor: '#FFF8E1',
                                    color: '#F9A825',
                                }}
                            />
                        )}
                    </Box>
                    
                    {/* Title */}
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: 700,
                            fontFamily: 'Poppins, sans-serif',
                            mb: 0.5,
                            lineHeight: 1.3,
                        }}
                    >
                        {currentAd.title}
                    </Typography>
                    
                    {/* Description */}
                    <Typography
                        variant="body2"
                        sx={{
                            color: '#666',
                            fontSize: '0.85rem',
                            mb: 1.5,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                        }}
                    >
                        {currentAd.description}
                    </Typography>
                    
                    {/* Features */}
                    {currentAd.inlineContent.features && currentAd.inlineContent.features.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                            {currentAd.inlineContent.features.slice(0, 3).map((feature, i) => (
                                <Typography
                                    key={i}
                                    variant="caption"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.3,
                                        color: '#2E7D32',
                                        fontSize: '0.7rem',
                                    }}
                                >
                                    <CheckCircle sx={{ fontSize: 12 }} />
                                    {feature}
                                </Typography>
                            ))}
                        </Box>
                    )}
                    
                    {/* Footer */}
                    <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {currentAd.inlineContent.price && (
                                <Typography
                                    sx={{
                                        fontWeight: 700,
                                        color: '#FF6B35',
                                        fontSize: '1rem',
                                    }}
                                >
                                    {currentAd.inlineContent.price}
                                </Typography>
                            )}
                            {currentAd.inlineContent.rating && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Rating
                                        value={currentAd.inlineContent.rating}
                                        precision={0.1}
                                        size="small"
                                        readOnly
                                    />
                                    <Typography variant="caption" sx={{ color: '#666' }}>
                                        ({currentAd.inlineContent.reviewCount?.toLocaleString()})
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                        
                        <Button
                            variant="contained"
                            size="small"
                            endIcon={<ChevronRight />}
                            sx={{
                                bgcolor: '#2D6E6A',
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 2,
                                '&:hover': {
                                    bgcolor: '#009688',
                                },
                            }}
                        >
                            {currentAd.ctaText}
                        </Button>
                    </Box>
                </Box>
            </Card>
            
            <AdDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                ad={currentAd}
                page={page}
                placement="in_feed"
            />
        </>
    );
};

export default InFeedAd;
